/**
 * ManageBilling — branded subscription-management page.
 *
 * Replaces the previous "Manage billing" button that dumped users straight to
 * Stripe's naked customer portal. Instead this page:
 *   • Shows their current plan (Checkup or Protection) with what they get
 *   • Offers a one-click switch to the OTHER plan (upgrade or downgrade)
 *   • Cancel button opens a "save the sale" modal that first offers a downgrade
 *     to Checkup, and only after that routes to Stripe portal for final cancel
 *   • Also has a "Manage payment method / Stripe invoices" button that opens
 *     the Stripe portal for the things WE genuinely can't do ourselves
 *
 * Aesthetic: matches the Dashboard header exactly — same off-white bg, same
 * mono captions, same brand accent. Nothing here should feel like a different
 * product.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Check, Loader2, ShieldCheck, Sparkles, ExternalLink,
  ArrowUp, ArrowDown, X, CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const PLANS = {
  essential: {
    id: "essential",
    name: "Monthly Checkup",
    price: 19,
    tagline: "Manual scans only",
    features: [
      "5 manual scans / month",
      "Whole-site donation-link check",
      "AI Executive Summary with each scan",
      "Verification Badge",
    ],
  },
  complete: {
    id: "complete",
    name: "Protection",
    price: 39,
    tagline: "Mission Watch Protection + Audit + Health Report",
    features: [
      "Everything in Monthly Checkup, plus:",
      "Mission Watch Protection — critical pages watched throughout the day",
      "Instant alerts when anything needs attention",
      "Mobile experience audit (mobile & tablet)",
      "Priority email delivery",
    ],
  },
};

export default function ManageBilling() {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [swapping, setSwapping] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/subscription");
      setSub(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load subscription");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const currentPlanId = sub?.plan || "essential";
  const otherPlanId = currentPlanId === "complete" ? "essential" : "complete";
  const isUpgrade = currentPlanId === "essential";

  /** One-click plan swap via the existing /checkout/session endpoint.
      Backend detects the existing Stripe subscription and modifies it in-place. */
  const swapPlan = useCallback(async (targetPlanId) => {
    if (swapping) return;
    setSwapping(targetPlanId);
    try {
      const { data } = await api.post("/checkout/session", {
        plan_id: targetPlanId,
        origin_url: window.location.origin,
      });
      if (data.plan_change) {
        toast.success(data.message || "Plan updated");
        await load();
      } else if (data.url) {
        // Fresh checkout (customer without active sub) — hand off to Stripe.
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't switch plans");
    } finally { setSwapping(null); }
  }, [swapping, load]);

  const openStripePortal = useCallback(async () => {
    if (openingPortal) return;
    setOpeningPortal(true);
    try {
      const { data } = await api.post("/billing-portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't open Stripe portal");
      setOpeningPortal(false);
    }
  }, [openingPortal]);

  const currentPlan = PLANS[currentPlanId] || PLANS.essential;
  const otherPlan = PLANS[otherPlanId];

  return (
    <div className="min-h-screen bg-parchment text-charcoal-900" data-testid="manage-billing-page">
      {/* Header */}
      <header className="border-b border-black/5 bg-white/60 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-charcoal-900"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </button>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400">Billing</span>
            <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-brand/10 text-brand">
              &#9679; Mission Watch
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 sm:py-14">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-charcoal-400">Manage subscription</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight">Your plan</h1>
          <p className="mt-2 text-charcoal-600">Upgrade, downgrade, or manage your subscription — all in one place.</p>
        </div>

        {loading ? (
          <div className="mt-12 flex items-center gap-2 text-charcoal-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your subscription…
          </div>
        ) : (
          <>
            {/* Two-plan grid — current plan highlighted, other plan as the swap target */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              <PlanCard
                plan={currentPlan}
                current
                trialing={sub?.status === "trialing"}
                trialEnd={sub?.trial_end}
              />
              <PlanCard
                plan={otherPlan}
                isUpgrade={isUpgrade}
                onSwap={() => swapPlan(otherPlanId)}
                swapping={swapping === otherPlanId}
              />
            </div>

            {/* Manage payment / Cancel row */}
            <div className="mt-10 rounded-2xl border border-black/10 bg-white p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[11px] font-mono uppercase tracking-widest text-charcoal-400">Payment & invoices</p>
                  <p className="mt-1 text-sm text-charcoal-700">
                    Update your card, download invoices, or view billing history in Stripe.
                  </p>
                </div>
                <button
                  onClick={openStripePortal}
                  disabled={openingPortal}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-charcoal-900/15 text-sm text-charcoal-900 hover:bg-charcoal-50 disabled:opacity-50 whitespace-nowrap"
                  data-testid="open-stripe-portal-btn"
                >
                  {openingPortal ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
                  Manage payment method
                  <ExternalLink className="w-3 h-3 text-charcoal-400" />
                </button>
              </div>

              <div className="mt-5 pt-5 border-t border-black/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-charcoal-900">Cancel subscription</p>
                  <p className="text-[13px] text-charcoal-500">
                    You'll keep access until the end of your current billing period.
                  </p>
                </div>
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="text-sm text-red-600 hover:text-red-700 hover:underline whitespace-nowrap"
                  data-testid="open-cancel-modal-btn"
                >
                  Cancel my subscription →
                </button>
              </div>
            </div>
          </>
        )}
      </main>

      <SaveTheSaleModal
        open={showCancelModal}
        currentPlanId={currentPlanId}
        onClose={() => setShowCancelModal(false)}
        onDowngrade={async () => {
          setShowCancelModal(false);
          await swapPlan("essential");
        }}
        onProceedToStripe={openStripePortal}
      />
    </div>
  );
}


/** Individual plan card — current OR the swap target. */
function PlanCard({ plan, current, isUpgrade, onSwap, swapping, trialing, trialEnd }) {
  return (
    <div
      className={
        "rounded-2xl border p-6 " +
        (current
          ? "bg-charcoal-900 text-white border-charcoal-900"
          : "bg-white border-black/10")
      }
      data-testid={current ? "current-plan-card" : "other-plan-card"}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className={"text-[10px] font-mono uppercase tracking-widest " + (current ? "text-white/60" : "text-charcoal-400")}>
            {current ? "Your plan" : (isUpgrade ? "Upgrade to" : "Downgrade to")}
          </p>
          <h2 className={"mt-1 text-2xl font-semibold tracking-tight " + (current ? "text-white" : "text-charcoal-900")}>
            {plan.name}
          </h2>
          <p className={"mt-0.5 text-[13px] " + (current ? "text-white/70" : "text-charcoal-500")}>{plan.tagline}</p>
        </div>
        {current ? (
          <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">
            <ShieldCheck className="inline w-3 h-3 mr-1" strokeWidth={2.5} /> Current
          </span>
        ) : (
          <span className={"inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full " + (isUpgrade ? "bg-brand/10 text-brand" : "bg-charcoal-100 text-charcoal-600")}>
            {isUpgrade ? <ArrowUp className="w-3 h-3" strokeWidth={2.5} /> : <ArrowDown className="w-3 h-3" strokeWidth={2.5} />}
            {isUpgrade ? "Upgrade" : "Downgrade"}
          </span>
        )}
      </div>

      <div className="mt-5 flex items-baseline gap-1">
        <span className={"text-4xl font-semibold " + (current ? "text-white" : "text-charcoal-900")}>${plan.price}</span>
        <span className={current ? "text-white/60 text-sm" : "text-charcoal-500 text-sm"}>/month</span>
      </div>

      {current && trialing ? (
        <p className="mt-2 text-[12px] text-emerald-300">
          Free trial · first payment {trialEnd ? new Date(trialEnd * 1000 || trialEnd).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "at trial end"}
        </p>
      ) : null}

      <ul className="mt-5 space-y-2">
        {plan.features.map((f) => (
          <li key={f} className={"flex items-start gap-2 text-[13px] " + (current ? "text-white/85" : "text-charcoal-700")}>
            <Check className={"w-3.5 h-3.5 mt-0.5 shrink-0 " + (current ? "text-emerald-300" : "text-brand")} strokeWidth={2.5} />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {!current ? (
        <button
          onClick={onSwap}
          disabled={swapping}
          className={
            "mt-6 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium disabled:opacity-50 " +
            (isUpgrade
              ? "bg-brand text-white hover:bg-brand/90"
              : "border border-charcoal-900/15 text-charcoal-900 hover:bg-charcoal-50")
          }
          data-testid={"swap-to-" + plan.id + "-btn"}
        >
          {swapping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (isUpgrade ? <Sparkles className="w-3.5 h-3.5" /> : null)}
          {swapping ? "Switching…" : (isUpgrade ? `Upgrade to ${plan.name}` : `Downgrade to ${plan.name}`)}
        </button>
      ) : null}
    </div>
  );
}


/** Save-the-sale modal — offered when the customer clicks Cancel. */
function SaveTheSaleModal({ open, currentPlanId, onClose, onDowngrade, onProceedToStripe }) {
  if (!open) return null;
  const onProtection = currentPlanId === "complete";
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={onClose}
      data-testid="cancel-modal"
    >
      <div
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-black/5 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-black/5">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400">Confirm cancellation</p>
            <h2 className="mt-1 text-lg font-semibold text-charcoal-900">Sorry to see you go</h2>
          </div>
          <button onClick={onClose} className="text-charcoal-400 hover:text-charcoal-700 p-1" data-testid="cancel-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {onProtection ? (
            <>
              <div className="rounded-xl border border-brand/20 bg-brand/5 p-4">
                <p className="text-[11px] font-mono uppercase tracking-widest text-brand mb-2">Before you go — save $20/month</p>
                <p className="text-sm text-charcoal-800 leading-relaxed">
                  You can drop down to the <strong>Checkup</strong> plan ($19/mo) instead of canceling.
                  You&apos;ll keep your Verification Badge and 5 manual checks/month — you just lose
                  Mission Watch Protection. Most people who downgrade stay for years.
                </p>
                <button
                  onClick={onDowngrade}
                  className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand text-white text-sm font-medium hover:bg-brand/90"
                  data-testid="downgrade-instead-btn"
                >
                  <ArrowDown className="w-3.5 h-3.5" /> Downgrade to Checkup instead
                </button>
              </div>
              <p className="text-[13px] text-charcoal-500">
                Still want to cancel completely? We'll take you to Stripe to finish. You'll keep access
                through the end of your current billing period.
              </p>
            </>
          ) : (
            <p className="text-sm text-charcoal-700">
              We'll take you to Stripe to complete your cancellation. Your Verification Badge will
              stay live through the end of your current billing period.
            </p>
          )}

          <div className="flex items-center justify-between gap-2 pt-2">
            <button
              onClick={onClose}
              className="text-sm text-charcoal-600 hover:text-charcoal-900"
              data-testid="cancel-modal-keep-plan"
            >
              Keep my plan
            </button>
            <button
              onClick={onProceedToStripe}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-red-200 bg-red-50 text-red-700 text-sm font-medium hover:bg-red-100"
              data-testid="proceed-to-stripe-cancel-btn"
            >
              Continue to Stripe <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
